<script setup lang="ts">
import { mdiClose, mdiEye, mdiEyeOff } from "@mdi/js";
import type { Ref } from "vue";
import { generatedID } from "~/utils/formUtils";

type InputTypeOptions = "text" | "password" | "tel" | "number";

interface IInput {
	modelValue: string | number | null;
	size?: "small" | "";
	label: string;
	id?: string | null;
	dataId?: string;
	iconPos?: "left" | "right";
	iconClass?: string | string[];
	noFloatingLabel?: boolean;
	readonly?: true | false;
	icon?: string;
	error?: string | null | undefined | Ref<string>;
	clearInputIcon?: boolean;
	type?: InputTypeOptions;
	prefixLabel?: string | null;
	fillPrefix?: boolean;
	labelClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	autocomplete?: boolean;
	focusOnClear?: boolean;
	unwrapError?: boolean;
	customPlaceholder?: string;
	iconButton?: boolean;
	validateNumber?: boolean;
}

const props = withDefaults(defineProps<IInput>(), {
	id: null,
	iconPos: "left",
	size: "",
	label: "",
	icon: "",
	noFloatingLabel: false,
	readonly: false,
	error: null,
	iconClass: "",
	type: "text",
	prefixLabel: null,
	fillPrefix: false,
	dataId: "",
	labelClass: "",
	unwrapError: false,
});

const inputType: Ref<InputTypeOptions> = ref(props.type);
const inputField = ref();
const emit = defineEmits(["update:modelValue", "blur", "update:clearInput", "enter", "tab", "iconClicked"]);

const value = computed({
	get() {
		return props.modelValue;
	},
	set(val) {
		emit("update:modelValue", val);
	},
});

const togglePassword = () => {
	if (inputType.value === "password") {
		inputType.value = "text";
	} else {
		inputType.value = "password";
	}
};

const passwordIcon = computed(() => {
	if (inputType.value === "password") {
		return mdiEye;
	}
	return mdiEyeOff;
});

const customPlaceholder = computed(() => {
	if (props.noFloatingLabel) {
		return props.label;
	} else {
		return props.customPlaceholder ? props.customPlaceholder : "";
	}
});

const inputID = generatedID(props.label, props.id);

const handleClearInput = () => {
	if (props.focusOnClear) {
		inputField.value.focus();
	}
};

const focusOnInput = () => {
	if (!inputField.value) return;
	inputField.value.focus();
};

defineExpose({ focusOnInput });

const handle = (e: KeyboardEvent) => {
	if (props.type === "number" && props.validateNumber) {
		if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
			e.preventDefault();
		}
	}
};
</script>

<template>
	<div
		:class="[
			'input-wrapper',
			props.size,
			!noFloatingLabel && 'mt-1.5',
			{ 'password-input': props.type === 'password' || props.iconButton || (clearInputIcon && value && value.toString().length > 0) },
			{ icon: props.icon || props.type === 'password' },
			{ 'icon-left': props.icon && props.iconPos === 'left' },
			{ 'icon-right': (props.icon && props.iconPos === 'right') || props.type === 'password' },
			{ error: props.error },
			{ active: value },
			{ prefixed: props.prefixLabel },
			{ customPlaceholder: props.customPlaceholder },
			{ fillPrefix: props.fillPrefix },
			{ clearable: clearInputIcon },
		]"
	>
		<div class="relative text-dark-600">
			<UiIcon
				:class="iconClass"
				v-if="(props.icon || props.type === 'password') && clearInputIcon ? !value : true"
				:name="props.type === 'password' ? passwordIcon : props.icon"
				:path="props.type === 'password' ? passwordIcon : props.icon"
				@click="props.iconButton ? emit('iconClicked') : togglePassword()"
			></UiIcon>
			<UiIcon
				@click.stop="
					() => {
						emit('update:modelValue', '');
						emit('update:clearInput');
						handleClearInput();
					}
				"
				class="right-2"
				v-if="clearInputIcon && value && value.toString().length > 0"
				:path="mdiClose"
			/>
			<span v-if="props.prefixLabel">
				{{ props.prefixLabel }}
			</span>
			<input
				:data-id="dataId"
				ref="inputField"
				:id="inputID"
				:autocomplete="autocomplete ? 'on' : 'off'"
				v-model="value"
				@blur="$emit('blur')"
				@keydown.enter="$emit('enter')"
				@keydown.tab="$emit('tab')"
                @keydown="handle"
				:type="inputType"
				:readonly="readonly"
				:placeholder="customPlaceholder ? customPlaceholder : ''"
				:class="labelClass"
			/>
			<label class="after:bg-light-50 after:dark:bg-dark-800" v-if="!noFloatingLabel" :for="inputID">{{ label }}</label>
		</div>
		<p v-if="props.error" :id="`${inputID}_help`" :class="unwrapError ? 'absolute' : ''">{{ error }}</p>
	</div>
</template>

<style scoped lang="scss">
.input-wrapper {
	input {
		@apply m-0 px-4 box-border block rounded border h-10 w-full text-base text-dark-600 bg-light-50 appearance-none focus:outline-none focus:ring-0 transition-colors dark:text-light-50 border-light-500 dark:border-dark-600 dark:bg-dark-800 placeholder:text-dark-800 dark:placeholder:text-light-50;
		~ label {
			@apply text-base -translate-y-1/2 top-1/2;
		}

		&:focus {
			@apply border-identity;
			~ label {
				@apply text-xs top-1 -translate-y-4 text-identity;
			}
		}
	}

	&.clearable {
		input {
			@apply pr-8;
		}
	}

	&.small {
		input {
			@apply text-xs h-8;
		}

		label {
			@apply text-xs;
		}
	}

	&:focus-within {
		svg {
			@apply fill-identity dark:fill-identity;
		}
	}

	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	input:-webkit-autofill:active {
		-webkit-box-shadow: 0 0 0px 1000px #ffffff inset;

		.dark & {
			-webkit-text-fill-color: #ffffff;
			-webkit-box-shadow: 0 0 0px 1000px #232323 inset;
		}
	}

	svg {
		@apply fill-dark-600 w-6 h-6 absolute top-1/2 -mt-3 dark:fill-light-50 transition-colors;
	}

	label {
		pointer-events: none;
		@apply left-3 absolute text-xs text-dark-600 px-1 duration-300 top-1 transform -translate-y-4 z-10 origin-[0] dark:text-light-50;
		&:after {
			@apply absolute left-0 right-0 h-[4px] z-[-1] top-1/2 -translate-y-1/2;
			content: " ";
		}
	}

	p {
		@apply pl-4 text-xs;
	}

	&.active {
		input {
			~ label {
				@apply text-xs top-1 -translate-y-4;
			}
		}
	}

	&.customPlaceholder {
		input {
			~ label {
				@apply text-xs top-1 -translate-y-4;
			}
		}

		input::placeholder {
			@apply text-dark-800 dark:text-light-50;
		}
	}

	&.prefixed {
		input {
			@apply pl-14 pr-4;
			~ label {
				@apply left-12;
			}
		}

		span {
			@apply px-2 absolute -translate-y-1/2 top-1/2 w-14 text-dark-800 dark:text-light-50 text-center;
		}

		svg {
			display: none;
		}
	}

	&.fillPrefix {
		input {
			@apply pr-4 pl-16;
		}

		span {
			@apply bg-light-300 dark:bg-dark-600 h-[calc(40px-2px)] left-[1px] flex justify-center items-center rounded-l;
		}
	}

	&.icon {
		&.icon-left {
			input {
				@apply pl-10 pr-4;
			}

			label {
				@apply left-9;
			}

			svg {
				@apply left-2;
			}
		}

		&.icon-right {
			input {
				@apply pl-4 pr-10;
			}

			svg {
				@apply right-2;
			}
		}
	}

	&.error {
		input {
			@apply border-error-600 text-error-600;
			&:focus {
				~ label {
					@apply text-error-600;
				}
			}
		}

		label {
			@apply text-error-600;
		}

		svg {
			@apply fill-error-600;
		}

		p {
			@apply text-error-600;
		}
	}

	&.password-input {
		svg {
			cursor: pointer;
		}
	}

	&:not(.password-input) {
		svg {
			pointer-events: none;
		}
	}
}

/*Input helper text animation */
.helpertext-enter-active,
.helpertext-leave-active {
	transition: max-height 0.5s ease-in-out;
}

.helpertext-enter-to,
.helpertext-leave-from {
	overflow: hidden;
	max-height: 30px;
}

.helpertext-enter-from,
.helpertext-leave-to {
	overflow: hidden;
	max-height: 0;
}
</style>
